import { useState, useEffect } from "react";
import {
  Backdrop,
  Box,
  Button,
  Fade,
  Grid,
  IconButton,
  Modal,
  Paper,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  styled,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { ClientInformation } from "./ClientInformation";
import { DeceasedInformation } from "./DeceasedInformation";
import { ServiceInformation } from "./ServiceInfo";
import { PackageSelection } from "./PackageSelection";
import { PreviewPanel } from "./PreviewPanel";

import patternUrl from "../../../../../assets/pattern.png";
import { RootState } from "../../../../../store/Index";
import { BookingData } from "../../../../../types/bookings";
import { checkPermission } from "../../../../../service/permissionGuard";
import { PERMISSIONS } from "../../../../../constants/permissions";
import apiService from "../../../../../service/Api";
import { BookingResponse } from "../../../../bookings/types/add-booking";
import { buildBookingFormData } from "../../../../bookings/utils/add-booking-form-data";
import { initialValues } from "../../../../bookings/AddBooking";
import { ConferenceServiceInformation } from "./ConferenceServiceInfo";
import { Groups3Outlined } from "@mui/icons-material";

// ── Booking type ─────────────────────────────────────────────────────────────

export type BookingType = "FUNERAL" | "CONFERENCE";

// ── Props ────────────────────────────────────────────────────────────────────

interface BookingModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CustomConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 15 },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: "rgba(255,255,255,0.6)",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: { backgroundColor: "#fff" },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: "rgba(255,255,255,0.3)",
    borderRadius: 1,
  },
}));

const CustomStepIcon = styled("div")<{
  active?: boolean;
  completed?: boolean;
  browndark?: string;
}>(({ active, completed, browndark }) => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: completed
    ? "#fff"
    : active
      ? "#fff"
      : "rgba(255,255,255,0.2)",
  color: completed
    ? (browndark ?? "#724E41")
    : active
      ? (browndark ?? "#724E41")
      : "rgba(255,255,255,0.6)",
  fontWeight: 700,
  fontSize: 14,
  boxShadow: active ? "0 0 0 3px rgba(255,255,255,0.4)" : "none",
  cursor: "pointer",
  transition: "all 0.2s ease",
}));

// ── Redesigned, Taller & Beautiful Type-picker Modal ─────────────────────────

interface TypePickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (type: BookingType) => void;
  color10: string;
  color400: string;
  color700: string;
  color800: string;
  color900: string;
}

function TypePickerModal({
  open,
  onClose,
  onSelect,
  color10,
  color400,
  color800,
  color900,
}: TypePickerModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 300 } }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            outline: "none",
            width: { xs: "95%", sm: "90%" },
            maxWidth: 500,
            minHeight: { xs: 140, sm: 180 },
            p: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: color900,
              boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
            }}
          >
            {/* Header matching the Main Stepper Layout */}
            <Box
              sx={{
                p: 4,
                position: "relative",
                overflow: "hidden",
                height: 125,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                backgroundImage: `linear-gradient(135deg, ${color400}F5 0%, ${color10}F0 50%, ${color400}F0 100%), url(${patternUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundBlendMode: "multiply",
                borderTopLeftRadius: "19px",
                borderTopRightRadius: "19px",
                backdropFilter: "blur(10px)",
              }}
            >
              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  color: "rgba(255,255,255,0.8)",
                  "&:hover": {
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.15)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>

              <Typography
                variant="h5"
                fontWeight={800}
                sx={{
                  color: "#fff",
                  textTransform: "uppercase",
                  fontSize: { xs: "17px", md: "20px" },
                  mb: 0.5,
                }}
              >
                Create New Booking
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#fff",
                  fontWeight: 400,
                  fontSize: "13px",
                }}
              >
                Select the category of the booking you wish to create.
              </Typography>
            </Box>

            {/* Content Body: Taller options cards with details */}
            <Box sx={{ p: 4 }}>
              <Grid container spacing={3}>
                {/* Funeral Option Card */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Button
                    onClick={() => onSelect("FUNERAL")}
                    sx={{
                      width: "100%",
                      minHeight: 180,
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textAlign: "center",
                      borderRadius: 4,
                      border: `1px solid ${color800}`,
                      backgroundColor: `${color800}40`,
                      textTransform: "none",
                      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        border: `1px solid ${color400}`,
                        backgroundColor: `${color400}12`,
                        transform: "translateY(-4px)",
                        boxShadow: `0 12px 24px rgba(0,0,0,0.3), 0 0 0 1px ${color400}`,
                        "& .type-picker-emoji": {
                          transform: "scale(1.15)",
                        },
                      },
                    }}
                  >
                    <Box
                      className="type-picker-emoji"
                      sx={{
                        fontSize: 42,
                        mb: 2,
                        lineHeight: 1,
                        transition: "transform 0.2s ease-in-out",
                      }}
                    >
                      ⚰️
                    </Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ color: color400, mb: 1, fontSize: "16px" }}
                    >
                      Funeral Services
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: color400,
                        fontSize: "12px",
                        lineHeight: 1.4,
                      }}
                    >
                      Set up a new funeral service arrangement for your client.
                    </Typography>
                  </Button>
                </Grid>

                {/* Conference Option Card */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Button
                    onClick={() => onSelect("CONFERENCE")}
                    sx={{
                      width: "100%",
                      minHeight: 180,
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textAlign: "center",
                      borderRadius: 4,
                      border: `1px solid ${color800}`,
                      backgroundColor: `${color800}40`,
                      textTransform: "none",
                      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        border: `1px solid ${color400}`,
                        backgroundColor: `${color400}12`,
                        transform: "translateY(-4px)",
                        boxShadow: `0 12px 24px rgba(0,0,0,0.3), 0 0 0 1px ${color400}`,
                        "& .type-picker-emoji": {
                          transform: "scale(1.15)",
                        },
                      },
                    }}
                  >
                    <Box
                      className="type-picker-emoji"
                      sx={{
                        mb: 2,
                        transition: "transform 0.2s ease-in-out",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Groups3Outlined
                        sx={{
                          fontSize: 42,
                          color: color400,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ color: color400, mb: 1, fontSize: "16px" }}
                    >
                      Conference / Event
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: color400,
                        fontSize: "12px",
                        lineHeight: 1.4,
                      }}
                    >
                      Set up a new conference or venue event for your client.
                    </Typography>
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Modal>
  );
}

// ── Main booking modal (stepper only) ────────────────────────────────────────

export function BookingModal({ open, onClose, onSuccess }: BookingModalProps) {
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const color10 = reduxTheme.primary_colors?.[10] ?? "#f5ede9";
  const color400 = reduxTheme.primary_colors?.[400] ?? "#724E41";
  const color700 = reduxTheme.primary_colors?.[700] ?? "#4a3028";
  const color800 = reduxTheme.primary_colors?.[800] ?? "#2a1f1c";
  const color900 = reduxTheme.primary_colors?.[900] ?? "#1a1210";

  // ── State ─────────────────────────────────────────────────────────────────

  const [showTypePicker, setShowTypePicker] = useState(false);
  const [showStepper, setShowStepper] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookingType, setBookingType] = useState<BookingType | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [bookingData, setBookingData] = useState<BookingData>(buildInitialData);
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionSuccess, setSubmissionSuccess] = useState<boolean | null>(
    null,
  );

  const steps =
    bookingType === "CONFERENCE"
      ? [
          "Client Information",
          "Conference Details",
          "Service Information",
          "Package Selection",
        ]
      : [
          "Client Information",
          "Deceased Information",
          "Service Information",
          "Package Selection",
        ];

  // Open type picker when `open` flips to true
  useEffect(() => {
    if (open) {
      setShowTypePicker(true);
      setShowStepper(false);
      setBookingType(null);
      setActiveStep(0);
      setBookingData(buildInitialData());
      setSubmissionError(null);
      setSubmissionSuccess(null);
    } else {
      setShowTypePicker(false);
      setShowStepper(false);
    }
  }, [open]);

  // Propagate booking_type into form data
  useEffect(() => {
    if (bookingType) {
      setBookingData((prev) => ({ ...prev, booking_type: bookingType }));
    }
  }, [bookingType]);

  const isLastStep = activeStep === steps.length - 1;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleTypeSelect = (type: BookingType) => {
    setBookingType(type);
    setShowTypePicker(false);
    setShowStepper(true);
  };

  const handlePickerClose = () => {
    setShowTypePicker(false);
    onClose();
  };

  const handleStepperClose = () => {
    setShowStepper(false);
    setActiveStep(0);
    setBookingType(null);
    setBookingData(buildInitialData());
    setSubmissionError(null);
    setSubmissionSuccess(null);
    setShowStepper(false);
    setShowSuccessModal(false);
    onClose();
  };

  const handleNext = () => {
    if (isLastStep) submitBooking(bookingData);
    else setActiveStep((p) => p + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      setShowStepper(false);
      setBookingType(null);
      setShowTypePicker(true);
    } else {
      setActiveStep((p) => Math.max(p - 1, 0));
    }
  };

  const handleSuccessDone = () => {
    setShowSuccessModal(false);
    setSubmissionSuccess(null);
    setActiveStep(0);
    setBookingType(null);
    setBookingData(buildInitialData());
    onClose();
  };

  //  Submission

  const submitBooking = async (dataToSubmit: BookingData) => {
    setSubmitting(true);
    setSubmissionError(null);
    setSubmissionSuccess(null);

    try {
      checkPermission(PERMISSIONS.BOOKING_ADD);

      // ── Step 1: Create the contact ──────────────────────────────────────────
      const contactRes = await apiService.post<{ id: number }>(
        "/api/contacts/contact/",
        {
          first_name: dataToSubmit.first_name,
          last_name: dataToSubmit.surname,
          email: dataToSubmit.email,
          phone: dataToSubmit.phone,
          contact_type: "individual",
        },
      );

      // ── Step 2: Resolve venue → search first, create only if not found ──────
      let venueId: string | null = null;

      if (dataToSubmit.venue_name) {
        try {
          const searchRes = await apiService.get<{
            results: { id: number; name: string }[];
          }>(
            `/api/venues/venue/?search=${encodeURIComponent(dataToSubmit.venue_name)}`,
          );

          const results = searchRes.data.results ?? [];
          const exactMatch = results.find(
            (v) =>
              v.name.toLowerCase() === dataToSubmit.venue_name!.toLowerCase(),
          );
          const match = exactMatch ?? results[0];

          if (match) {
            // Venue already exists — reuse its id
            venueId = String(match.id);
          } else {
            // Venue not found — create it using whatever address info we have
            const createRes = await apiService.post<{ id: number }>(
              "/api/venues/venue/",
              {
                name: dataToSubmit.venue_name,
                address_one:
                  dataToSubmit.address_one || dataToSubmit.venue_name,
                address_two: dataToSubmit.address_two || "",
                suburb: dataToSubmit.suburb || "",
                city: dataToSubmit.city || "",
              },
            );
            venueId = String(createRes.data.id);
          }
        } catch {
          console.warn("Venue resolution failed — proceeding without venue_id");
        }
      }

      // ── Step 3: Enrich booking data ─────────────────────────────────────────
      const enrichedData: BookingData = {
        ...dataToSubmit,
        contact_id: contactRes.data.id,
        // Ensure address_one and service_address are never null
        address_one:
          dataToSubmit.address_one || dataToSubmit.venue_name || "N/A",
        service_address:
          dataToSubmit.service_address ||
          dataToSubmit.address_one ||
          dataToSubmit.venue_name ||
          "N/A",
        service_city: dataToSubmit.service_city || dataToSubmit.city || "N/A",
        ...(venueId ? { venue_ids: [venueId] } : {}),
      };

      // ── Step 4: Submit the booking ──────────────────────────────────────────
      const formData = buildBookingFormData(enrichedData);
      const response = await apiService.post<BookingResponse>(
        "/api/bookings/booking/create-fullbooking/",
        formData,
      );

      console.log(response);
      setShowStepper(false);
      setShowSuccessModal(true);
      toast.success("Booking has been created successfully", {
        position: "top-right",
        autoClose: 2000,
        theme: "light",
      });

      setTimeout(() => {
        onSuccess?.();
      }, 800);
    } catch (err: unknown) {
      let errorMessage = "Failed to create booking.";
      const responseData = (
        err as {
          response?: { status?: number; data?: Record<string, unknown> };
        }
      )?.response;

      if (responseData?.status === 401) {
        navigate("/login");
        errorMessage = "Session expired. Please log in.";
      } else if ((responseData?.data as { detail?: string })?.detail) {
        errorMessage =
          (responseData?.data as { detail?: string }).detail ?? errorMessage;
      } else if (responseData?.data) {
        if (typeof responseData.data === "string") {
          errorMessage = responseData.data;
        } else {
          try {
            errorMessage = Object.entries(responseData.data)
              .map(([key, value]) =>
                Array.isArray(value)
                  ? `${key}: ${value.join(", ")}`
                  : `${key}: ${String(value)}`,
              )
              .join("; ");
          } catch {
            errorMessage = JSON.stringify(responseData.data);
          }
        }
      }

      setSubmissionError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });

      console.error(
        "Submission error:",
        (err as { response?: { data?: unknown } }).response?.data ??
          (err as { message?: string }).message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step content ──────────────────────────────────────────────────────────

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <ClientInformation
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        );
      case 1:
        return bookingType === "CONFERENCE" ? (
          <ConferenceServiceInformation
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        ) : (
          <DeceasedInformation
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        );
      case 2:
        return (
          <ServiceInformation
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        );
      case 3:
        return (
          <PackageSelection
            bookingData={bookingData}
            setBookingData={setBookingData}
            serviceType={bookingType ?? "FUNERAL"}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* ── Phase 1: redesigned compact type picker ─────────────────────── */}
      <TypePickerModal
        open={showTypePicker}
        onClose={handlePickerClose}
        onSelect={handleTypeSelect}
        color10={color10}
        color400={color400}
        color700={color700}
        color800={color800}
        color900={color900}
      />

      {/* ── Phase 2: full stepper modal ───────────────────────────────────── */}
      <Modal
        open={showStepper}
        onClose={handleStepperClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 300 } }}
      >
        <Fade in={showStepper}>
          <Box
            sx={{
              position: "absolute",
              top: { xs: 0, md: "50%" },
              left: "50%",
              transform: {
                xs: "translateX(-50%)",
                md: "translate(-50%, -50%)",
              },

              width: { xs: "100vw", md: "95vw" },
              maxWidth: 1200,

              height: { xs: "100vh", md: "auto" },
              maxHeight: { xs: "100vh", md: "95vh" },

              outline: "none",
              overflow: "hidden",

              p: { xs: 0, md: 1 },
            }}
          >
            <Grid
              container
              spacing={{ xs: 2, md: 3 }}
              sx={{
                height: "100%",
                flexWrap: { xs: "nowrap", md: "wrap" },
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <Grid
                size={{ xs: 12, md: 8 }}
                sx={{
                  display: "flex",
                  minHeight: 0,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    width: "100%",
                    borderRadius: { xs: 0, md: 4 },

                    height: {
                      xs: "100vh",
                      md: "calc(95vh - 16px)",
                    },

                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",

                    backgroundColor: color900,
                    boxShadow: {
                      xs: "none",
                      md: "0 24px 64px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      p: { xs: 2, md: 3 },
                      pb: { xs: 1.5, md: 2 },
                      pt: { xs: 4, md: 5 },
                      position: "relative",
                      overflow: "hidden",
                      height: { xs: 90, md: 120 },
                      boxShadow: 0,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      backgroundImage: `linear-gradient(135deg, ${color400}F5 0%, ${color10}F0 50%, ${color400}F0 100%), url(${patternUrl})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundBlendMode: "multiply",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    <IconButton
                      onClick={handleStepperClose}
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        color: "rgba(255,255,255,0.8)",
                        "&:hover": {
                          color: "#fff",
                          bgcolor: "rgba(255,255,255,0.15)",
                        },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>

                    <Stepper
                      activeStep={activeStep}
                      alternativeLabel
                      connector={<CustomConnector />}
                    >
                      {steps.map((label, index) => (
                        <Step
                          key={label}
                          onClick={() => setActiveStep(index)}
                          sx={{ cursor: "pointer" }}
                        >
                          <StepLabel
                            StepIconComponent={() => (
                              <CustomStepIcon
                                active={index === activeStep}
                                completed={index < activeStep}
                                browndark={color400}
                              >
                                {index + 1}
                              </CustomStepIcon>
                            )}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color:
                                  index <= activeStep
                                    ? "#fff"
                                    : "rgba(255,255,255,0.5)",
                                fontWeight: index === activeStep ? 700 : 400,
                                letterSpacing: "0.02em",
                                display: { xs: "none", sm: "block" },
                                mt: 0.5,
                              }}
                            >
                              {label}
                            </Typography>
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>

                  {/* Body */}
                  <Box
                    sx={{
                      p: { xs: 2, md: 4 },
                      flex: 1,
                      overflowY: "auto",
                      backgroundColor: color900,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {submissionSuccess ? (
                      <Box sx={{ textAlign: "center", pt: 6 }}>
                        <Typography
                          variant="h6"
                          color="success.main"
                          gutterBottom
                        >
                          Booking submitted successfully!
                        </Typography>
                        <Typography variant="body2" sx={{ color: color400 }}>
                          Redirecting to booking details…
                        </Typography>
                      </Box>
                    ) : (
                      renderStepContent(activeStep)
                    )}

                    {submissionError && (
                      <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                        {submissionError}
                      </Typography>
                    )}
                  </Box>

                  {/* Footer */}
                  <Box
                    sx={{
                      p: 3,
                      borderTop: `1px solid ${color800}`,
                      backgroundColor: color900,
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      justifyContent: "flex-end",
                      alignItems: "stretch",
                      gap: 2,
                    }}
                  >
                    <Button
                      onClick={handleBack}
                      disabled={submitting}
                      sx={{
                        color: color400,
                        backgroundColor: "transparent",
                        border: `1px solid ${color400}`,
                        px: 3,
                        borderRadius: "8px",
                        textTransform: "none",
                        fontSize: "13px",
                        width: { xs: "100%", sm: "auto" },
                        "&:hover": { backgroundColor: `${color400}18` },
                        "&.Mui-disabled": {
                          borderColor: color800,
                          color: color700,
                          opacity: 0.5,
                        },
                      }}
                    >
                      Back
                    </Button>

                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={submitting || !!submissionSuccess}
                      sx={{
                        color: "#fff",
                        px: 4,
                        borderRadius: "8px",
                        textTransform: "none",
                        width: { xs: "100%", sm: "auto" },
                        fontSize: "13px",
                        background: `linear-gradient(135deg, ${color10} 0%, ${color400} 100%)`,
                        "&:hover": {
                          background: `linear-gradient(135deg, ${color10} 0%, ${color400} 100%)`,
                        },
                        "&.Mui-disabled": { opacity: 0.4 },
                      }}
                    >
                      {isLastStep
                        ? submitting
                          ? "Submitting…"
                          : "Submit Booking"
                        : "Next"}
                    </Button>
                  </Box>
                </Paper>
              </Grid>

              {/* ── Right: Preview panel ─────────────────────────────────── */}
              <Grid
                size={{ xs: 12, md: 4 }}
                sx={{
                  display: { xs: "none", md: "block" },
                }}
              >
                <Box
                  sx={{
                    position: "sticky",
                    top: 0,
                    height: "calc(95vh - 16px)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <PreviewPanel formData={bookingData} />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      </Modal>
      <Modal
        open={showSuccessModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 300 } }}
      >
        <Fade in={showSuccessModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              outline: "none",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                p: 5,
                textAlign: "center",
                backgroundColor: color900,
                width: 320,
                boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: "rgba(76,175,80,0.15)",
                  border: "2px solid #4caf50",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                }}
              >
                <Typography sx={{ fontSize: 28 }}>✓</Typography>
              </Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: "#fff", mb: 1 }}
              >
                Booking Created
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: color400, mb: 4, lineHeight: 1.6 }}
              >
                We will contact the client shortly and keep you updated on every
                step.
              </Typography>
              <Button
                onClick={handleSuccessDone}
                sx={{
                  background: `linear-gradient(135deg, ${color400} 0%, ${color10} 100%)`,
                  color: "#fff",
                  px: 4,
                  borderRadius: "30px",
                  textTransform: "none",
                  fontSize: "13px",
                }}
              >
                Done
              </Button>
            </Paper>
          </Box>
        </Fade>
      </Modal>
    </>
  );
}

// ── Helper ────────────────────────────────────────────────────────────────────

function buildInitialData(): BookingData {
  return { ...initialValues };
}
